import { useEffect, useState } from 'react';
import { recoveredErrorTracker } from '@/lib/recoveredErrorTracker';
import { useSafeMode } from '@/lib/safeMode';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

/**
 * Subtle indicator that shows when errors have been automatically recovered
 * during the current session. Appears in the bottom-right corner.
 *
 * Features:
 * - Shows count of recovered errors
 * - Expandable to show recent errors
 * - Link to enable Safe Mode
 * - Auto-hides after 30 seconds of no new errors
 */
export function RecoveredErrorIndicator() {
  const initialCount = recoveredErrorTracker.getCount();
  const [count, setCount] = useState(initialCount);
  const [isVisible, setIsVisible] = useState(initialCount > 0);
  const [lastErrorTime, setLastErrorTime] = useState<number | null>(initialCount > 0 ? 1 : null);
  const safeMode = useSafeMode();

  useEffect(() => {
    // Subscribe to new errors
    const unsubscribe = recoveredErrorTracker.subscribe((newCount) => {
      setCount(newCount);
      setIsVisible(true);
      setLastErrorTime(Date.now());
    });

    return unsubscribe;
  }, []);

  // Auto-hide after 30 seconds of no new errors
  useEffect(() => {
    if (!lastErrorTime || !isVisible) return;

    const timer = setTimeout(() => {
      setIsVisible(false);
    }, 30000);

    return () => clearTimeout(timer);
  }, [lastErrorTime, isVisible]);

  // Don't show if no errors or already in safe mode
  if (!isVisible || count === 0) {
    return null;
  }

  const recentErrors = recoveredErrorTracker.getRecent(3);
  const isFrequent = recoveredErrorTracker.isFrequent();

  return (
    <div
      className={cn(
        'fixed bottom-4 right-4 z-50',
        'animate-in slide-in-from-bottom-2 fade-in duration-300'
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setIsVisible(false)}
            className={cn(
              'flex items-center gap-2 rounded-full px-3 py-1.5',
              'text-xs font-medium shadow-lg',
              'transition-colors duration-200',
              isFrequent
                ? 'bg-warning-100 text-brand-blue-700 border border-warning-500/30'
                : 'bg-neutral-100 text-neutral-700 border border-neutral-200'
            )}
          >
            <span
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold',
                isFrequent ? 'bg-warning-500 text-white' : 'bg-neutral-500 text-white'
              )}
            >
              {count > 99 ? '99+' : count}
            </span>
            <span>{isFrequent ? 'Errori frequenti' : 'Errori recuperati'}</span>
            <span className="text-[10px] opacity-60">×</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="end" className="max-w-xs">
          <div className="space-y-2">
            <p className="font-medium">
              {count} {count === 1 ? 'errore recuperato' : 'errori recuperati'}
            </p>
            <p className="text-xs text-muted-foreground">
              L&apos;app ha corretto automaticamente alcuni problemi. Se riscontri difficoltà, prova
              la Modalità Sicura.
            </p>

            {recentErrors.length > 0 && (
              <div className="border-t pt-2 mt-2">
                <p className="text-[10px] uppercase text-muted-foreground mb-1">Ultimi errori:</p>
                <ul className="text-xs space-y-0.5">
                  {recentErrors.map((err, i) => (
                    <li key={i} className="truncate text-muted-foreground">
                      {err.component && <span className="font-medium">[{err.component}]</span>}{' '}
                      {err.message.slice(0, 40)}...
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!safeMode.enabled && (
              <Button
                size="sm"
                variant="secondary"
                className="w-full mt-2"
                onClick={() => {
                  safeMode.enable();
                  setIsVisible(false);
                }}
              >
                Attiva Modalità Sicura
              </Button>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
